/**
 * Questa è la classe per il Repository che riguarda per i passaggi finali della scelta del
 * familiare assistito o della creazione di un nuovo familiare con nuova anagrafica
 * Quindi tutte le richieste ai servizi che c'entrano con le fasi finali di registrazione della
 * prestazione andrebbero messe qui
 *
 */

import axios, { AxiosResponse } from "axios";
import axiosRetry from "axios-retry";

import "core/extensions/string.extensions";

import {
	IRegisterRequest,
	IRegisterResponse,
	IMatricola,
	IGetFamiliariResponse,
	IFamiliare,
	IRegisterFamiliareEsistenteRequest,
	IRegisterFamiliareEsistenteResponse,
	IGetLoggedUserDataResponse,
	IPaymentSendRequest,
	IGetChangeUserDataRequest,
	IGetprenotazioniResponse,
	IPrenotazione,
} from "core/interfaces/data/IAssistitoRequest";
import { IRegisteredUserData } from "core/interfaces/data/IRegisterRequest";
import { getResponseMessage, IBaseRequest, IBaseResponse } from "core/interfaces/data/Base";

// questo serve per fare la richiesta una seconda volta se in caso la prima non va a buon fine
axiosRetry(axios, {
	retries: 2,
});

// serve per settare i cookie in risposta
axios.defaults.withCredentials = true;

class AssistitoRepository {
	/**
	 * Metodo che fa la registrazione sia di una nuova Anagrafica che di un nuova Persona di riferimento
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async Register(authData: IRegisterRequest) {
		const endpoint = authData.Logged ? "RegisteredAtlantePositionCreate" : "AtlantePositionCreate";
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/${endpoint}`;
		try {
			const res = await axios.post<IRegisterRequest, AxiosResponse<IRegisterResponse>>(url, authData);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const matricola: IMatricola = {
				Matricola: res.data.Matricola,
				IdPersonaRif: res.data.IdPersonaRif,
				CodServizio: res.data.CodServizio,
				IdPreventivo: res.data.IdPreventivo,
				DestPrestazione: res.data.DestinatarioPrestazione,
				CodPai: res.data.CodPai,
				MailInviata: res.data.MailInviata,
			};

			return matricola;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che prende tutte i familiari dell'utente che si è appena collegato
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async GetFamiliari(props: IBaseRequest) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/Assisted`;
		try {
			const res = await axios.get<IGetFamiliariResponse>(url);

			if (res !== undefined) {
				if (res.data.Errors) {
					throw new Error(getResponseMessage(res.data.Errors));
				}

				const familiari: IFamiliare[] = JSON.parse(res.data.Anagrafiche);

				return familiari;
			} else return undefined;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che prende tutte i familiari dell'utente che si è appena collegato
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async GetPrenotazioniUtente(culture: string) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/Reservations?culture=${culture}`;
		try {
			const res = await axios.get<IGetprenotazioniResponse>(url);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const prenotazioni: IPrenotazione[] = JSON.parse(res.data.Reservations);

			for (let i = 0; i < prenotazioni.length; i++) {
				let item = prenotazioni[i];
				let da = item.OccupazioneDa.toString().toDateWS();
				let a = item.OccupazioneA.toString().toDateWS();

				prenotazioni[i].OccupazioneDa = da;
				prenotazioni[i].OccupazioneA = a;
			}

			return prenotazioni;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che fa la registrazione della prestazione di un familiare già esistente scelto
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async RegisterFamiliareEsistente(props: IRegisterFamiliareEsistenteRequest) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/RegisteredAtlantePositionAddPlanning`;
		try {
			const res = await axios.post<IRegisterFamiliareEsistenteRequest, AxiosResponse<IRegisterFamiliareEsistenteResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const matricola: IMatricola = {
				Matricola: res.data.Matricola,
				CodServizio: res.data.CodServizio,
				IdPreventivo: res.data.IdPreventivo,
				DestPrestazione: res.data.DestinatarioPrestazione,
				CodPai: res.data.CodPai,
				MailInviata: res.data.MailInviata,
			};

			return matricola;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che restituisce le informazioni di base del richiedente
	 */
	async GetLoggedUserData(props: IBaseRequest) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/GetRegisteredUserData`;

		try {
			const res = await axios.post<IBaseRequest, AxiosResponse<IGetLoggedUserDataResponse>>(url, props, { responseType: "json" });
			if (res !== undefined) {
				if (res.data.Errors) {
					throw new Error(getResponseMessage(res.data.Errors));
				}

				const registeredUser: IRegisteredUserData = {
					Nome: res.data.Nome,
					Cognome: res.data.Cognome,
					CodiceFiscale: res.data.CodiceFiscale,
					TelefonoContatti: res.data.TelefonoContatti.toString(),
					Email: res.data.Email,
					Sesso: res.data.Sesso,
					DataNascita: res.data.DataNascita.toLocaleString(), // moment(res.data.DataNascita),
					ComuneNascita: JSON.parse(res.data.ComuneNascita),
					IdCittadinanza: res.data.IdCittadinanaza,
					ConsensoSMS: res.data.ConsensoSMS,
					ConsensoMail: res.data.ConsensoMail,
					IndirizzoResidenza: res.data.IndirizzoResidenza,
				};

				return registeredUser;
			}
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che invia i dati del pagamento
	 */
	async PaymentSend(props: IPaymentSendRequest) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/PaymentSave`;
		try {
			const res = await axios.post<IPaymentSendRequest, AxiosResponse<IBaseResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const result: IBaseResponse = {
				Result: res.data.Result,
				Errors: res.data.Errors,
			};

			return result;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}

	/**
	 * Metodo che restituisce le informazioni di base del richiedente
	
	 */
	async UpdateUserData(props: IGetChangeUserDataRequest) {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/UpdateUserData`;

		try {
			const res = await axios.post<IGetChangeUserDataRequest, AxiosResponse<IBaseResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			return res.data.Result;
		} catch (err) {
			console.log(err);
			return undefined;
		}
	}
}
var AssistitoRep=new AssistitoRepository()
export default AssistitoRep;
